// @flow
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { CreateMultipleTasksAction, CreationTaskDrawer } from 'features/daily-plan';
import { ORIGIN_TABLE_MAP } from 'features/daily-plan/constants';
import { useTaskDrawerManager } from 'features/daily-plan/hooks';
import {
  CreationSavedSearchDrawer,
  SavedSearchDrawer,
  SearchApplied,
  useSavedSearches
} from 'features/saved-searches';
import useDatatable, {
  addRingCentralContactToStore,
  buildMultiplePhonesActionsRenderer,
  buildRowActionsRendererDefinition,
  extractObjectFromDataTable,
  getColumnPreferences,
  getColumnsToRender
} from 'hooks/datatable';
import useMultipleSelection, { CustomTableToolbarSelect } from 'hooks/multipleSelection';
import { useSearchProject } from 'hooks/searchProject';
import { useQuickViewPreview } from 'hooks/useQuickViewPreview';
import isEmpty from 'lodash/isEmpty';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import { useUserContext } from 'providers/UserProvider';
import { EntityRoutes } from 'routes/constants';
import { userHasRoles } from 'services/Authorization';
import { When } from 'UI/components/atoms/When';
import AutocompleteSelect from 'UI/components/molecules/AutocompleteSelect';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import DataTable from 'UI/components/organisms/DataTable';
import FPQuickView from 'UI/components/organisms/inventoryProfiles/FPQuickView';
import FiltersLayout from 'UI/components/templates/FiltersLayout';
import { additionalPeriodRanges } from 'UI/components/templates/SideFiltersLayout/filters';
import { backNavigateListConfig } from 'UI/constants/config';
import { CommunicationActionPreset, PageTitles } from 'UI/constants/defaults';
import { Endpoints } from 'UI/constants/endpoints';
import { ContactEntityMapping, ContactRole, EntityType } from 'UI/constants/entityTypes';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { Roles } from 'UI/constants/roles';
import { createCustomStaticRanges, getFinalColumns } from 'UI/utils';
import { getMinRequiredFilters } from 'UI/utils/filters';
import { DataTableLink } from 'UI/utils/renderers';

import {
  columnsContact,
  DateFilters,
  FiltersGroups,
  filtersInclude,
  PeriodColumnBySection,
  VirtualProps
} from './columns';

const ContactRoutes = {
  [ContactRole.HiringAuthority]: EntityRoutes.HiringAuthorityProfile,
  [ContactRole.Name]: EntityRoutes.ContactProfile,
  [ContactRole.Candidate]: EntityRoutes.CandidateProfile
};

const extraRenderers = {
  contactLink: value => {
    if (!value) return null;

    const { id, role_id: roleId, full_name: fullName } = value;
    const route = ContactRoutes[roleId];

    return id && route ? (
      <DataTableLink id={id} text={fullName} route={route} enableInTabRedirect />
    ) : null;
  }
};

const CONTACT_KEY = 'contacts';
const orderByOptions = {
  column: 'full_name',
  direction: 'asc'
};
const numberOfActionColumns = 1;

const MIN_REQUIRED_FILTERS = getMinRequiredFilters();

const ContactsList = () => {
  const dispatch = useDispatch();
  const [currentUser] = useUserContext();
  const isHighLevelUser = userHasRoles(
    [
      Roles.Operations,
      Roles.DataCoordinator,
      Roles.Leadership,
      Roles.AssistantRegionalDirector,
      Roles.Coach
    ],
    currentUser
  );

  const [dateTypeFilter, setDateTypeFilter] = useState(DateFilters[0]);

  const { checkIfFeatureFlagEnabled } = useFeatureFlags();
  const apiVersion = checkIfFeatureFlagEnabled(FeatureFlags.MultiplePhonesColumn) ? 2 : 1;
  const isTasksCreationEnabled = checkIfFeatureFlagEnabled(FeatureFlags.DailyPlanAddTasksFromTable);
  const isSavedSearchesEnabled = checkIfFeatureFlagEnabled(FeatureFlags.ContactsSavedSearches);

  const {
    activeSearch,
    getSavedSearchButtonDefinitions,
    handleApplySearch,
    handleCloseSavedSearchDrawer,
    handleCloseSavedSearchForm,
    handleDeleteSearch,
    handleEditSavedSearchDrawerOpen,
    handleEditSearch,
    handleRemoveSearch,
    isDirty,
    savedSearchesUiState
  } = useSavedSearches({ preferencesKey: CONTACT_KEY });

  const multiplePhonesActionButtons = buildMultiplePhonesActionsRenderer({
    customName: 'phoneNumbers',
    handleAction,
    columns: getFinalColumns(columnsContact)
  });

  const CONTACT_COLUMNS = [...getFinalColumns(columnsContact), multiplePhonesActionButtons].filter(
    column => !!column
  );

  const initialPreferences = getColumnPreferences(
    CONTACT_KEY,
    0,
    orderByOptions,
    CONTACT_COLUMNS,
    numberOfActionColumns
  );
  const {
    columnPreferences,
    count,
    data,
    filters,
    getData,
    handleColumnDisplayChange,
    handleColumnOrderChange,
    handleColumnSortChange,
    handleFiltersApply,
    handleFiltersChange,
    handleFiltersToggle,
    handleKeywordChange,
    handlePageChange,
    handlePeriodChange,
    handlePerPageChange,
    handleSectionChange,
    isBelowMinFiltersThreshold,
    listState,
    queryParams
  } = useDatatable({
    apiVersion,
    columnsDefinitions: CONTACT_COLUMNS,
    enableSavedSearches: isSavedSearchesEnabled && savedSearchesUiState.isSearching,
    endpoint: Endpoints.Names,
    entityName: 'Contacts',
    entityType: EntityType.Contact,
    initialPreferences,
    key: CONTACT_KEY,
    minRequiredFilters: !isHighLevelUser ? MIN_REQUIRED_FILTERS : null,
    numberOfActionColumns,
    periodDefaultValue: null,
    periodFilterColumn: PeriodColumnBySection,
    savedSearch: isSavedSearchesEnabled && activeSearch && activeSearch?.search,
    sendDateWithTimezone: true,
    shouldScrollOnNavigate: true,
    virtualProps: VirtualProps
  });

  useEffect(() => {
    handleEditSearch(filters);
  }, [filters, handleEditSearch]);

  const { columns, columnOrder } = columnPreferences;
  const { isLoading, page, perPage, showWholeSkeleton, isSideMenuOpen, keyword } = listState;

  function handleAction({ rowData, origin }) {
    const item = extractObjectFromDataTable(columns, ['role_id'], rowData);
    const roleId = item?.role_id;
    const itemRole = ContactEntityMapping[roleId];
    itemRole &&
      addRingCentralContactToStore({
        rowData,
        columns: CONTACT_COLUMNS,
        dispatch,
        role: itemRole,
        origin
      });
  }

  const { entityId, quickViewEntity, quickViewState, listViewPreset, toggleQuickViewPreview } =
    useQuickViewPreview({
      columns,
      data,
      quickViewEntityRoleProps: { roleKey: 'role_id' }
    });

  const phoneActionButtons = buildRowActionsRendererDefinition({
    actions: [...CommunicationActionPreset, listViewPreset],
    namespace: EntityType.Contact,
    handleAction,
    columns: CONTACT_COLUMNS
  });

  const dataForSelection = useMemo(
    () =>
      data.map(item => ({
        id: item.id,
        type: item.role,
        type_id: item.role_id,
        status: item.status
      })),
    [data]
  );
  const {
    filteredItems,
    handleRowSelection,
    multiSelectComponents,
    selectedIndexesInPage,
    totalSelected
  } = useMultipleSelection({ data: dataForSelection, count });

  const {
    isDailyPlanTaskDrawerOpen,
    formattedTaskItems,
    allowedTasks,
    tasksQuery,
    handleCloseTaskDrawer,
    handleOpenTaskDrawer,
    handleOnTaskCreated
  } = useTaskDrawerManager({
    table: ORIGIN_TABLE_MAP.Contacts,
    selectedItems: filteredItems.data,
    selectionType: filteredItems.type,
    queryParams
  });

  const { SearchProjectAction, SearchProjectForms } = useSearchProject({
    filteredItems,
    queryParams
  });

  useEffect(() => {
    document.title = PageTitles.Contact;
  }, []);

  const handleDateTypeFilterChange = (name, value) => {
    setDateTypeFilter(value);
    handleSectionChange && handleSectionChange(name, value.id);
  };

  const finalColumns = getColumnsToRender([...columns, phoneActionButtons], extraRenderers);

  const handleTabRefresh = async () => {
    await getData();
  };

  const shouldSelectRequiredFilters = isBelowMinFiltersThreshold && (!data || data.length === 0);
  const savedSearchButtonDefinitions = getSavedSearchButtonDefinitions({ filters });

  return (
    <FiltersLayout
      filters={filters}
      groups={FiltersGroups}
      includeFilters={filtersInclude}
      onSearch={handleFiltersApply}
      onFiltersChange={handleFiltersChange}
      onMenuToggle={handleFiltersToggle}
      enableStore={false}
      title="Contacts"
      isSideMenuOpen={isSideMenuOpen}
      titleLabelProps={backNavigateListConfig}
      defaultRange={null}
      extraSelector={
        <AutocompleteSelect
          name="dateTypeFilter"
          placeholder="Filter By"
          selectedValue={dateTypeFilter}
          onSelect={handleDateTypeFilterChange}
          defaultOptions={DateFilters}
          disableClearable
        />
      }
      dateRangerPickerProps={{
        staticRanges: createCustomStaticRanges(additionalPeriodRanges)
      }}
      onPeriodChange={handlePeriodChange}
      enableSavedSearches={isSavedSearchesEnabled}
      minRequiredFilters={shouldSelectRequiredFilters ? MIN_REQUIRED_FILTERS : null}
      shouldSelectRequiredFilters={shouldSelectRequiredFilters}
      savedSearchesHeader={
        isSavedSearchesEnabled &&
        activeSearch && (
          <SearchApplied
            isDirty={isDirty}
            onRemoveSearch={handleRemoveSearch}
            onSaveSearch={!isEmpty(filters) ? handleEditSavedSearchDrawerOpen : null}
            searchName={activeSearch?.name}
          />
        )
      }
      savedSearchesActions={
        isSavedSearchesEnabled && (
          <FPButtonBuilder
            GridContainerProps={{ justify: 'flex-end', spacing: 2 }}
            buttonDefinitions={savedSearchButtonDefinitions}
          />
        )
      }
    >
      <DataTable
        columnOrder={columnOrder?.length ? columnOrder : undefined}
        columns={finalColumns}
        components={multiSelectComponents}
        count={count}
        CustomToolbar={
          <CustomTableToolbarSelect total={totalSelected}>
            <When condition={isTasksCreationEnabled}>
              <CreateMultipleTasksAction onClick={handleOpenTaskDrawer} />
            </When>
            <SearchProjectAction />
          </CustomTableToolbarSelect>
        }
        data={data}
        draggableColumns={{
          enabled: true
        }}
        enableCustomFilters
        filter={false}
        isExpandable
        loading={showWholeSkeleton}
        onColumnDisplayClick={handleColumnDisplayChange}
        onColumnOrderChange={handleColumnOrderChange}
        onColumnSortChange={handleColumnSortChange}
        onPageClick={handlePageChange}
        onPerPageClick={handlePerPageChange}
        onRowSelectionChange={handleRowSelection}
        onSearchTextChange={handleKeywordChange}
        onToggleFilters={handleFiltersToggle}
        page={page}
        refreshing={isLoading}
        rowsPerPage={perPage}
        rowsSelected={selectedIndexesInPage}
        searchText={keyword}
        selectToolbarPlacement="none"
      />
      <SearchProjectForms />
      <When condition={entityId && quickViewState.isQuickViewOpen}>
        <FPQuickView
          drawerProps={{ open: quickViewState.isQuickViewOpen }}
          entityType={quickViewEntity}
          id={entityId}
          itemsToNavigate={data}
          onClose={toggleQuickViewPreview}
          quickViewEntityRoleProps={{ roleKey: 'role_id' }}
          onEditionCompleted={handleTabRefresh}
        />
      </When>
      <When
        condition={isDailyPlanTaskDrawerOpen && (formattedTaskItems.length > 0 || !!tasksQuery)}
      >
        <CreationTaskDrawer
          onClose={handleCloseTaskDrawer}
          listItems={formattedTaskItems}
          allowedTaskTypes={allowedTasks}
          tasksQuery={tasksQuery}
          totalSelected={totalSelected}
          onTaskCreated={handleOnTaskCreated}
        />
      </When>

      <When condition={isSavedSearchesEnabled && savedSearchesUiState.isSavedSearchDrawerOpen}>
        <SavedSearchDrawer
          activeSearch={activeSearch}
          entityType={EntityType.Contact}
          onApplySearch={handleApplySearch}
          onClose={handleCloseSavedSearchDrawer}
          onDeleteSearch={handleDeleteSearch}
          open={savedSearchesUiState.isSavedSearchDrawerOpen}
        />
      </When>

      <When
        condition={
          isSavedSearchesEnabled &&
          (savedSearchesUiState.isCreationSavedSearchDrawerOpen ||
            savedSearchesUiState.isEditSavedSearchDrawerOpen)
        }
      >
        <CreationSavedSearchDrawer
          activeSearch={activeSearch}
          entityType={EntityType.Contact}
          onApplySearch={handleApplySearch}
          onSaveCompleted={handleCloseSavedSearchForm}
          onClose={handleCloseSavedSearchForm}
          open
          savedSearchId={savedSearchesUiState.isEditSavedSearchDrawerOpen ? activeSearch?.id : null}
          search={filters}
        />
      </When>
    </FiltersLayout>
  );
};

export default ContactsList;
